import { FC } from "react"
import Image from "next/image"
import styled from "styled-components"
import { ContentBlock, ContentfulProductWithShopifyData } from "@lib/types"

import { ProductListing } from "./ProductListing"
import { TextBlock, WistiaHeroVideo } from "@components/common"
import { Button } from "@components/ui"

interface Props {
  data: {
    alignStatic: string
    contentBlock: ContentBlock
    productsCollection: {
      items: ContentfulProductWithShopifyData[]
    }
  }
}

export const ProductsTallStatic: FC<Props> = ({ data }) => {
  const { alignStatic, contentBlock, productsCollection } = data
  const { image, wistiaId } = contentBlock

  return (
    <StyledProductsTallStatic $alignStatic={alignStatic}>
      <ProductListingWrapper>
        <ProductListing products={productsCollection.items} repeat={2} />
      </ProductListingWrapper>
      <ImageWrapper>
        {image?.url ? (
          <>
            <Image
              src={contentBlock.image.url}
              alt={contentBlock.image.description}
              width={534}
              height={900}
              layout="responsive"
            />
            <TextBlockWrapper>
              {contentBlock?.textBlock && (
                <TextBlock textBlock={contentBlock.textBlock} />
              )}
              {contentBlock?.button && <Button button={contentBlock.button} />}
            </TextBlockWrapper>
          </>
        ) : wistiaId ? (
          <WistiaHeroVideo wistia_id={wistiaId} />
        ) : null}
      </ImageWrapper>
    </StyledProductsTallStatic>
  )
}

interface StyleProps {
  $alignStatic: string
}

const StyledProductsTallStatic = styled.div<StyleProps>`
  @media (min-width: ${({ theme }) => theme.breakpoint.lg}px) {
    display: grid;
    grid-template-areas: ${({ $alignStatic }) =>
      $alignStatic === "Left" ? `"static products"` : `"products static"`};
    grid-template-columns: 1fr 1fr;
    grid-gap: 2.9rem;
    justify-content: space-between;
    margin: 0 0 3rem;
  }
`

const ImageWrapper = styled.div`
  position: relative;
  margin: 0 0 3.3rem;
  width: 100%;

  img {
    object-fit: cover;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.lg}px) {
    grid-area: static;
    margin: 0 0 2.5rem;
    height: ${({ theme }) =>
      `calc(100% - ${theme.productThumbnail.textWrapper.height + 2.5}rem)`};
  }
`

const ProductListingWrapper = styled.div`
  grid-area: products;
`

const TextBlockWrapper = styled.div`
  z-index: 1;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  max-width: 25rem;

  h1,
  h2,
  h3,
  h4,
  h5 {
    margin: 0 0 0.8rem;
    font-size: 2.4rem;
    line-height: 3.1rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  p {
    font-size: 1.5rem;
    line-height: 2.2rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    h1,
    h2,
    h3,
    h4,
    h5 {
      margin: 0 0 0.6rem;
      font-size: 3.8rem;
      line-height: 4.9rem;
    }
  }
`
